import React, { ReactElement, Suspense, lazy } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { LoadingScreen, useScrollToTop } from '@mesa-labs/mesa-ui';
import AuthRoute from './common/AuthRoute';
import FeatureFlagRoute from './common/FeatureFlagRoute';
import OnboardingLayout from '../components/onboarding/common/OnboardingLayout';
import DemoModeSelect from '../components/onboarding/DemoModeSelect';
import ReleaseNotes from '../components/dashboard/ReleaseNotes';

const OnboardingRoutes = lazy(() => import('./Onboarding'));
const DashboardRoutes = lazy(() => import('./Dashboard'));
const SignInPage = lazy(() => import('../components/auth/SignIn'));
const ForgotPassword = lazy(() => import('../components/auth/ForgotPassword'));
const ForgotPasswordConfirm = lazy(() => import('../components/auth/ForgotPasswordConfirm'));
const ForgotPasswordSubmit = lazy(() => import('../components/auth/ForgotPasswordSubmit'));
const SignUpPage = lazy(() => import('../components/auth/SignUp'));
const ConfirmAccountPage = lazy(() => import('../components/auth/ConfirmAccount'));
const UnsubscribePage = lazy(() => import('../components/auth/Unsubscribe'));

function AppRoutes(): ReactElement {
  const location = useLocation();

  useScrollToTop({ location });

  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* For backwards compatibility  */}
        <Route
          path="/dashboard/*"
          element={(
            <DashboardRoutes />
          )}
        />

        <Route
          path="/*"
          element={(
            <DashboardRoutes />
          )}
        />

        <Route
          path="/sign-in"
          element={(
            <AuthRoute redirectToSignIn={false} saveQueryStringData>
              <OnboardingLayout showProgressBar={false} useHeaderBorder={false}>
                <SignInPage />
              </OnboardingLayout>
            </AuthRoute>
          )}
        />

        <Route
          path="/forgot-password"
          element={(
            <AuthRoute redirectToSignIn={false}>
              <OnboardingLayout showProgressBar={false} useHeaderBorder={false}>
                <ForgotPassword />
              </OnboardingLayout>
            </AuthRoute>
          )}
        />

        <Route
          path="/forgot-password-confirm"
          element={(
            <AuthRoute redirectToSignIn={false}>
              <ForgotPasswordConfirm />
            </AuthRoute>
          )}
        />

        <Route
          path="/forgot-password-submit"
          element={(
            <AuthRoute redirectToSignIn={false}>
              <OnboardingLayout showProgressBar={false} useHeaderBorder={false}>
                <ForgotPasswordSubmit />
              </OnboardingLayout>
            </AuthRoute>
          )}
        />

        <Route
          path="/sign-up"
          element={(
            <AuthRoute redirectToSignIn={false} saveQueryStringData>
              <OnboardingLayout showProgressBar={false} rightHeaderVariant="sign_in" useHeaderBorder={false}>
                <SignUpPage />
              </OnboardingLayout>
            </AuthRoute>
          )}
        />

        <Route
          path="/confirm"
          element={(
            <AuthRoute redirectToSignIn={false} saveQueryStringData>
              <OnboardingLayout showProgressBar={false} rightHeaderVariant="start_over" useHeaderBorder={false}>
                <ConfirmAccountPage />
              </OnboardingLayout>
            </AuthRoute>
          )}
        />

        <Route
          path="/unsubscribe"
          element={(
            <FeatureFlagRoute flag="emailUnsubscribePage">
              <OnboardingLayout showProgressBar={false} useHeaderBorder={false}>
                <UnsubscribePage />
              </OnboardingLayout>
            </FeatureFlagRoute>
          )}
        />

        <Route
          path="onboarding/*"
          element={(
            <OnboardingRoutes />
          )}
        />

        <Route
          path="/release-notes"
          element={(
            <ReleaseNotes />
          )}
        />

        {process.env.NODE_ENV !== 'production' && (
          <Route
            path="/demo"
            element={(
              <OnboardingLayout showProgressBar={false} useHeaderBorder={false}>
                <DemoModeSelect />
              </OnboardingLayout>
            )}
          />
        )}
      </Routes>
    </Suspense>
  );
}

export default AppRoutes;
